import { HeroWelcome } from "@/components/HeroWelcome";
import { TryNow } from "@/components/TryNow";
import { Showcase } from "@/components/Showcase";
import { IndividualProducts } from "@/components/IndividualProducts";
import { usePricing, ServicesTable, QuotaPolicy } from "@/components/PricingDetails";
import { track } from "@/lib/funnel";
import React, { useEffect, useState } from "react";
import { PublicShareButton } from "@/components/PublicShareButton";
import { InstallAppButton } from "@/components/InstallAppButton";
import { AiBadge } from "@/components/AiBadge";
import { Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import {
  Sparkles, Image as ImageIcon, Video, CalendarClock, BarChart3,
  Rocket, Layers, ArrowLeft, Check, Zap, PenTool, ShieldCheck,
  Compass, Flag, Star, Crown, LogIn, CreditCard, Printer } from "lucide-react";
import { BrandHome } from "@/components/BrandHome";
import { Button } from "@/components/ui/button";
import api from "@/lib/api";
import Seo from "@/components/Seo";
import SiteFooter from "@/components/SiteFooter";
import { StoryHighlights } from "@/components/StoryHighlights";
import ScrollToTop from "@/components/ScrollToTop";
import PromoBanner from "@/components/PromoBanner";
import { PaymentMethods } from "@/components/PaymentMethods";
import { AccountMenu } from "@/components/AccountMenu";
import { useAuth } from "@/context/AuthContext";
import { useLang } from "@/context/LangContext";
import { LangToggle } from "@/components/LangToggle";
import { ThemeToggle } from "@/components/ThemeToggle";
import { LandingFaq, LANDING_FAQ_ITEMS } from "@/components/LandingFaq";
import { LandingServices, LANDING_SERVICES } from "@/components/LandingServices";

const features = [
  { icon: Sparkles, title: "استراتيجية ذكية", desc: "تحليل SWOT، دراسة السوق السعودي، ملخص تنفيذي، ومقترحات الهوية المرئية خلال ثوانٍ." },
  { icon: CreditCard, title: "بطاقة عمل vCard ذكية بوجهين", desc: "ليست بطاقة عادية: رمز QR يحفظ جهة اتصالك كاملة بنقرة، وجه خلفي بشعارك، وملف PDF للطباعة على الوجهين (8 بطاقات على A4) — وتعدّل بياناتك لاحقًا من مكتبتك بلا رصيد." },
  { icon: Printer, title: "منيو وبروشور ولوقو جاهزة للطباعة", desc: "مدخلاتك تُنسَّق داخل التصميم بنص عربي مدقَّق، مع PDF على A4 بما يناسب كل منتج: منيو طولي، بروشور عرضي بخطوط الطيّ، ولوقو بورقة هوية." },
  { icon: ImageIcon, title: "توليد الصور بالذكاء الاصطناعي", desc: "صور احترافية لمنشورات Instagram وTikTok وSnapchat بجودة عالية وبنكهة سعودية." },
  { icon: Video, title: "فيديوهات Omni Flash", desc: "فيديوهات قصيرة احترافية جاهزة للنشر مولّدة بتقنية Gemini Omni Flash." },
  { icon: PenTool, title: "نصوص وسكربتات", desc: "منشورات وسيناريوهات فيديو قابلة للتعديل تناسب كل منصة وجمهورها." },
  { icon: CalendarClock, title: "جدولة الحملات", desc: "قوالب شهرية جاهزة (14 صورة + 2 فيديو) بأفضل أوقات النشر للسوق السعودي." },
  { icon: BarChart3, title: "تقارير الأداء", desc: "تحليلات التفاعل والوصول ونصائح تحسين مستمرة لحملاتك." },
];

const fallbackPlans = [
  { name: "المجانية", price: "0", period: "مجاناً", features: ["خدمات حسب الطلب — بدون اشتراك: اشترِ الخدمة التي تحتاجها فقط", "صورة 9.5 · شعار 18 · بطاقة عمل 29 · غلاف 8 · منيو 15 · إنفوجرافيك 9 · بروشور 10 · فيديو 12 ريال", "نص منشور + صورة 9.5 · سكربت + فيديو 12 · نص المنشور وسكربت الفيديو مجانًا", "كوبون الهدية: صورتان + فيديو مجانًا + 25 ريال رصيد", "شحن المحفظة في أي وقت"], cta: "ابدأ مجاناً", highlight: false },
  { name: "المحتوى", price: "249", period: "ريال / شهرياً", features: ["مسار «محتوى متنوع» بدون استراتيجية ولا حملات", "30 صورة + 8 فيديو شهرياً", "نصوص ومنشورات وسكربتات لكل منصة", "تحرير الصور وإضافة الشعار"], cta: "اشترك الآن", highlight: false },
  { name: "الهوية الجاهزة", price: "499", period: "ريال / شهرياً", features: ["مسار «مشروع بهوية جاهزة»", "حتى 3 مشاريع · 60 صورة + 15 فيديو", "12 حملة شهرية مجدولة بأفضل الأوقات", "تقارير ومؤشرات أداء"], cta: "اشترك الآن", highlight: false },
  { name: "الاستراتيجية", price: "1299", period: "ريال / شهرياً", features: ["مسار «استراتيجية تسويق كاملة»", "SWOT ودراسة سوق وهوية بصرية", "5 مشاريع · 180 صورة + 45 فيديو", "60 حملة مجدولة · أهداف وقياس وتقارير تنفيذية"], cta: "اشترك الآن", highlight: true },
];

const tracks = [
  { icon: PenTool, color: "#D89134", testid: "track-content", to: "/register?track=content",
    title: "إنشاء مشروع محتوى متنوع",
    desc: "توليد صور وفيديو ونصوص جاهزة للنشر حسب كل قناة — بدون استراتيجية. ابدأ الإنتاج فوراً." },
  { icon: Layers, color: "#734F96", testid: "track-ready", to: "/register?track=ready",
    title: "إنشاء مشروع بهوية جاهزة",
    desc: "عندك شعار وهوية؟ ارفعها وانتقل مباشرةً لإنتاج المحتوى والحملات المجدولة." },
  { icon: Sparkles, color: "#1C4B3E", testid: "track-strategy", to: "/register?track=strategy",
    title: "إنشاء استراتيجية تسويق",
    desc: "رحلة كاملة من الصفر: تحليل SWOT ودراسة السوق والهوية والأهداف والتنفيذ والتقارير." },
];

// Map a backend plan to the landing display shape.
function mapPlan(p) {
  const periodBase = p.price === 0 ? "مجاناً" : `${p.currency || "ريال"} / ${p.period || ""}`.trim();
  return {
    name: p.name,
    price: p.price === 0 || p.price == null ? "0" : String(p.price),
    period: p.price === 0 ? "مجاناً" : periodBase,
    features: p.features || [],
    cta: p.price === 0 ? "ابدأ مجاناً" : "اشترك الآن",
    highlight: !!p.popular,
  };
}

const LANDING_FAQ = {
  "@context": "https://schema.org", "@type": "FAQPage",
  mainEntity: LANDING_FAQ_ITEMS.map(([q, a]) => ({ "@type": "Question", name: q, acceptedAnswer: { "@type": "Answer", text: a } })),
};

export default function Landing() {
  const { user } = useAuth();
  const { t } = useLang();
  const nav = useNavigate();
  const nextRaw = new URLSearchParams(window.location.search).get("next") || "";
  const nextQ = nextRaw.startsWith("/app") ? encodeURIComponent(nextRaw) : "";
  useEffect(() => { if (user && nextRaw.startsWith("/app")) nav(nextRaw, { replace: true }); }, [user, nextRaw, nav]);
  const [plans, setPlans] = useState(fallbackPlans);
  const pricing = usePricing();
  const [stats, setStats] = useState(null);
  const [reviews, setReviews] = useState(null);

  useEffect(() => {
    api.get("/plans")
      .then((r) => {
        if (Array.isArray(r.data) && r.data.length) {
          const sorted = [...r.data].sort((a, b) => (a.price || 0) - (b.price || 0));
          setPlans(sorted.map(mapPlan));
        }
      })
      .catch(() => {});
    api.post("/stats/visit").catch(() => {});
    track("visit");
    api.get("/stats/public").then((r) => setStats(r.data)).catch(() => {});
    api.get("/reviews").then((r) => setReviews(r.data)).catch(() => {});
  }, []);

  return (
    <div className="min-h-screen bg-background text-foreground overflow-x-hidden">
      <Seo
        title="توليد فيديو وصور إعلانية بالذكاء الاصطناعي | تصميم إعلانات احترافي للشركات السعودية"
        description="رواج الذكية: توليد فيديو بالذكاء الاصطناعي وإنشاء صور احترافية وتصميم إعلانات للشركات السعودية — إعلان اليوم الوطني، حملات رمضان، موسم الرياض، لوغو وموشن جرافيك، واستراتيجية تسويق كاملة ونشر تلقائي للسوشيال ميديا بلهجة سعودية."
        keywords="توليد فيديو بالذكاء الاصطناعي, إنشاء صور احترافية عبر الذكاء الاصطناعي, تصميم إعلانات احترافي, إنشاء محتوى تسويقي, الذكاء الاصطناعي في التسويق, إعلانات الشركات السعودية, تصميم إعلان اليوم الوطني, إعلانات لشهر رمضان, إعلان تسويق موسمي السعودية, موسم الرياض, إعلانات خليجية جذابة, إعلانات للسوشيال ميديا السعودية, توليد محتوى AI للسوشيال ميديا, لوغو ثلاثي الأبعاد, تصميم موشن جرافيك, تصميم عروض تقديمية, تحسين جودة الصور عبر AI, خدمات تعديل الصور الاحترافية, أفضل إعلان بالذكاء الاصطناعي, كيف أسوي إعلان, منصة تسويق سعودية, شركة تسويق الكتروني الرياض, تسويق الكتروني الخليج"
        jsonLd={[{
          "@context": "https://schema.org", "@type": "SoftwareApplication", name: "رواج الذكية", alternateName: "Rawaj Smart",
          applicationCategory: "BusinessApplication", operatingSystem: "Web, iOS, Android", url: "https://rawajsmart.com",
          inLanguage: "ar", areaServed: ["SA", "AE", "KW", "QA", "BH", "OM", "EG", "JO"],
          offers: { "@type": "AggregateOffer", priceCurrency: "SAR", lowPrice: "0", highPrice: "1299", offerCount: 4 },
          ...(reviews && reviews.count > 0 ? {
            aggregateRating: { "@type": "AggregateRating", ratingValue: reviews.average, reviewCount: reviews.count, bestRating: 5, worstRating: 1 },
            review: reviews.reviews.slice(0, 10).map((r) => ({ "@type": "Review", reviewRating: { "@type": "Rating", ratingValue: r.rating, bestRating: 5 }, author: { "@type": "Person", name: r.name }, reviewBody: r.text || "", datePublished: (r.created_at || "").slice(0, 10) })),
          } : {}),
          description: "منصة تسويق بالذكاء الاصطناعي للشركات السعودية: توليد فيديو وصور إعلانية، تصميم إعلانات اليوم الوطني ورمضان وموسم الرياض، لوغو وهوية، استراتيجية تسويق ونشر تلقائي للسوشيال ميديا.",
          keywords: "توليد فيديو بالذكاء الاصطناعي, تصميم إعلانات احترافي, إعلانات الشركات السعودية, إعلان اليوم الوطني, إعلانات رمضان, الذكاء الاصطناعي في التسويق",
        }, {
          "@context": "https://schema.org", "@type": "Service", name: "خدمات رواج الذكية للتسويق بالذكاء الاصطناعي", provider: { "@type": "Organization", name: "رواج الذكية", url: "https://rawajsmart.com" },
          areaServed: "SA", serviceType: "تصميم إعلانات وتوليد فيديو وصور بالذكاء الاصطناعي",
          hasOfferCatalog: { "@type": "OfferCatalog", name: "خدمات رواج الذكية", itemListElement: LANDING_SERVICES.map(([n, d]) => ({ "@type": "Offer", itemOffered: { "@type": "Service", name: n, description: d } })) },
        }, LANDING_FAQ]}
        path="/"
      />
      {/* Nav + promo banner */}
      <div className="fixed top-0 w-full z-50">
        <PromoBanner />
        <header className="glass border-b border-border w-full">
          <div className="max-w-7xl mx-auto px-3 sm:px-6 min-h-16 py-2 flex flex-wrap items-center justify-between gap-2">
          <BrandHome />
          <nav className="hidden md:flex items-center gap-8 text-sm text-muted-foreground">
            <a href="#features" className="hover:text-foreground transition-colors">{t("المميزات", "Features")}</a>
            <a href="#how" className="hover:text-foreground transition-colors">{t("كيف يعمل", "How it works")}</a>
            <a href="#pricing" className="hover:text-foreground transition-colors">{t("الأسعار", "Pricing")}</a>
            <a href="#faq" className="hover:text-foreground transition-colors">{t("الأسئلة الشائعة", "FAQ")}</a>
          </nav>
          <div className="flex items-center gap-2 flex-wrap justify-end">
          {!user && <PublicShareButton />}
          <span className={user ? "hidden sm:inline-flex" : "inline-flex"}><InstallAppButton /></span>
          <LangToggle />
          <ThemeToggle />
          {user && <Link to="/app/packages" data-testid="nav-subscribe-btn" title="اشترك" className="inline-flex h-10 items-center gap-1.5 rounded-xl border border-accent/50 px-2.5 sm:px-3 text-sm font-bold text-accent hover:bg-accent/10 transition-colors"><Crown className="w-4 h-4" /><span className="hidden sm:inline">اشترك</span></Link>}
          {user ? <AccountMenu /> : (
          <div className="flex items-center gap-3">
            <Link to={nextQ ? `/login?next=${nextQ}` : "/login"}><Button className="h-9 rounded-full bg-sky-600 hover:bg-sky-500 text-white font-bold gap-1.5 px-4" data-testid="nav-login-btn"><LogIn className="w-4 h-4" />{t("دخول", "Log in")}</Button></Link>
            <Link to={`/register?next=${encodeURIComponent("/app/packages")}`} data-testid="nav-subscribe-btn" title="اشترك" className="inline-flex h-10 items-center gap-1.5 rounded-xl border border-accent/50 px-2.5 sm:px-3 text-sm font-bold text-accent hover:bg-accent/10 transition-colors"><Crown className="w-4 h-4" /><span className="hidden sm:inline">اشترك</span></Link>
            <Link to="/register" onClick={() => track("cta_click", "nav")}>
              <Button className="h-9 rounded-full bg-primary hover:bg-primary/90 text-white" data-testid="nav-register-btn">{t("ابدأ الآن", "Get started")}</Button>
            </Link>
          </div>
          )}
          </div>
        </div>
      </header>
      </div>

      {/* Hero */}
      <section className="relative grid-bg pt-52 sm:pt-44 lg:pt-28 pb-20 lg:pb-12 px-6">
        <div className="absolute inset-0 sadu-overlay z-0 pointer-events-none" style={{ backgroundImage: `url(${process.env.PUBLIC_URL}/rawaj-bg.jpg)` }} />
        <div className="max-w-7xl mx-auto grid lg:grid-cols-2 gap-12 items-center relative z-10">
          <motion.div initial={{ opacity: 0, x: 30 }} animate={{ opacity: 1, x: 0 }} transition={{ duration: 0.7 }}>
            <div className="inline-flex items-center gap-2 bg-primary/10 border border-primary/30 rounded-full px-4 py-1.5 mb-6 lg:mb-4 text-sm text-primary font-semibold">
              <Zap className="w-4 h-4" /> {t("منصة التسويق الذكية للسوق السعودي", "The AI marketing platform for the Saudi market")}
            </div>
            <div><AiBadge testId="landing-ai-badge" /></div>
            <h1 className="text-4xl sm:text-5xl lg:text-[2.75rem] xl:text-5xl font-extrabold font-heading leading-tight mb-6 lg:mb-4">
              {t("ضع تسويق مشروعك على", "Put your business marketing on the")} <span className="gradient-text">{t("منصة الإطلاق", "launch pad")}</span> {t("بالذكاء الاصطناعي", "with AI")}
            </h1>
            <p className="text-lg lg:text-base text-muted-foreground leading-relaxed mb-8 lg:mb-6">
              {t("رواج الذكية يحوّل فكرتك إلى استراتيجية تسويق متكاملة ومحتوى احترافي (صور، فيديو، نصوص) جاهز للنشر وحملات مجدولة تلقائياً — كل ذلك في مكان واحد.", "Rawaj Smart turns your idea into a complete marketing strategy and professional content (images, video, copy) ready to publish, with campaigns scheduled automatically — all in one place.")}
            </p>
            <div className="flex flex-wrap gap-4">
              <Link to={user ? "/app" : "/register"} onClick={() => !user && track("cta_click", "hero")}>
                <Button size="lg" className="bg-primary hover:bg-primary/90 text-white font-bold gap-2 h-12 px-7" data-testid="landing-hero-cta">
                  <Rocket className="w-5 h-5" /> {t("ابدأ مجاناً — صورتان وفيديو هدية", "Start free — 2 images + 1 video gift")}
                </Button>
              </Link>
              <a href="#features">
                <Button size="lg" variant="outline" className="border-border h-12 px-7 gap-2" data-testid="landing-demo-cta">
                  {t("شاهد المميزات", "See features")} <ArrowLeft className="w-5 h-5" />
                </Button>
              </a>
            </div>
            {!user && <div className="mt-8 lg:mt-6"><TryNow /></div>}
            <div className="flex items-center gap-6 mt-10 lg:mt-7 text-sm text-muted-foreground">
              <div><span className="text-2xl font-bold text-foreground">+340%</span><br/>متوسط نمو التفاعل</div>
              <div className="w-px h-10 bg-border" />
              <div><span className="text-2xl font-bold text-foreground">16</span><br/>قطعة محتوى شهرياً</div>
              <div className="w-px h-10 bg-border" />
              <div><span className="text-2xl font-bold text-foreground">3</span><br/>منصات مدعومة</div>
            </div>
            {stats && (
              <div className="inline-flex items-center gap-4 mt-8 lg:mt-5 bg-emerald-500/10 border border-emerald-500/30 rounded-2xl px-5 py-3" data-testid="live-stats">
                <span className="flex items-center gap-2 text-sm text-emerald-400 font-semibold">
                  <span className="relative flex h-2.5 w-2.5">
                    <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75" />
                    <span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-emerald-500" />
                  </span>
                  منصة نشطة
                </span>
                <span className="w-px h-8 bg-emerald-500/30" />
                <div className="text-center" data-testid="stat-visitors-count"><div className="text-xl font-extrabold text-foreground">{Number(stats.visitors).toLocaleString("en-US")}</div><div className="text-[11px] text-muted-foreground">زائر</div></div>
                <span className="w-px h-8 bg-emerald-500/30" />
                <div className="text-center" data-testid="stat-users-count"><div className="text-xl font-extrabold text-foreground">{Number(stats.users).toLocaleString("en-US")}</div><div className="text-[11px] text-muted-foreground">مستخدم</div></div>
              </div>
            )}
          </motion.div>

          <motion.div initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 0.7, delay: 0.2 }} className="relative w-full lg:w-[75%] lg:max-w-[calc(100vh-190px)] mx-auto">
            <HeroWelcome />
          </motion.div>
        </div>
      </section>

      <Showcase />
      <IndividualProducts />

      {/* Start now — three tracks */}
      <section id="how" className="py-20 px-6 max-w-7xl mx-auto">
        <div className="text-center mb-14">
          <div className="inline-flex items-center gap-2 bg-accent/10 border border-accent/30 rounded-full px-4 py-1.5 mb-5 text-sm text-accent font-semibold">
            <Rocket className="w-4 h-4" /> ابدأ الآن — اختر مسارك
          </div>
          <h2 className="text-3xl sm:text-4xl font-bold font-heading mb-3">ثلاثة مسارات للبدء — كما يناسبك</h2>
          <p className="text-muted-foreground max-w-2xl mx-auto">سواء تبي محتوى سريع، أو عندك هوية جاهزة، أو تبدأ من الصفر — رواج الذكية يرافقك في كل خطوة.</p>
        </div>
        <div className="grid md:grid-cols-3 gap-6">
          {tracks.map((t, i) => (
            <motion.div key={t.testid} initial={{ opacity: 0, y: 24 }} whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }} transition={{ delay: i * 0.1 }}
              className="glass rounded-3xl p-8 card-hover border border-border flex flex-col" data-testid={`onboarding-${t.testid}`}>
              <div className="w-14 h-14 rounded-2xl flex items-center justify-center mb-5" style={{ background: `${t.color}22`, color: t.color }}>
                <t.icon className="w-7 h-7" />
              </div>
              <h3 className="text-xl font-bold mb-2">{t.title}</h3>
              <p className="text-muted-foreground mb-6 flex-1 leading-relaxed text-sm">{t.desc}</p>
              <Link to={t.to}>
                <Button className="w-full font-bold gap-2 text-white" style={{ background: t.color }} data-testid={`${t.testid}-cta`}>
                  ابدأ الآن <ArrowLeft className="w-4 h-4" />
                </Button>
              </Link>
            </motion.div>
          ))}
        </div>

      {/* Vision & Mission */}
      <section id="vision" className="py-20 px-6 max-w-7xl mx-auto" data-testid="vision-mission-section">
        <div className="text-center mb-12">
          <div className="inline-flex items-center gap-2 bg-primary/10 border border-primary/30 rounded-full px-4 py-1.5 mb-5 text-sm text-primary font-semibold">
            <Sparkles className="w-4 h-4" /> من الفكرة إلى الرواج بذكاء
          </div>
          <h2 className="text-3xl sm:text-4xl font-bold font-heading mb-3">رؤيتنا ورسالتنا</h2>
          <p className="text-muted-foreground max-w-2xl mx-auto">نرتّب لك طريق التسويق من البداية حتى النتائج.</p>
        </div>
        <div className="grid md:grid-cols-2 gap-6">
          <motion.div initial={{ opacity: 0, y: 24 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}
            className="glass rounded-3xl p-8 border border-border card-hover" data-testid="vision-card">
            <div className="w-14 h-14 rounded-2xl flex items-center justify-center mb-5 bg-primary/15 text-primary">
              <Compass className="w-7 h-7" />
            </div>
            <h3 className="text-xl font-bold mb-3">الرؤية</h3>
            <p className="text-muted-foreground leading-loose">
              أن نكون الوجهة الرقمية الأولى في المملكة التي تُمكّن أصحاب الأعمال ورواد المشاريع من بناء حضور
              تسويقي احترافي بذكاء وسهولة — نحوّل كل فكرة إلى رواجٍ ونتائج ملموسة.
            </p>
          </motion.div>
          <motion.div initial={{ opacity: 0, y: 24 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: 0.1 }}
            className="glass rounded-3xl p-8 border border-border card-hover" data-testid="mission-card">
            <div className="w-14 h-14 rounded-2xl flex items-center justify-center mb-5 bg-accent/15 text-accent">
              <Flag className="w-7 h-7" />
            </div>
            <h3 className="text-xl font-bold mb-3">الرسالة</h3>
            <p className="text-muted-foreground leading-loose">
              رواج الذكية وجهة رقمية ودّية واحترافية تساعد أصحاب الأعمال على الانطلاق والنمو عبر خدمات تسويق
              ذكية ومبسّطة وقابلة للتنفيذ — نرتّب لك طريق التسويق من البداية حتى تحقيق النتائج.
            </p>
          </motion.div>
        </div>
        <div className="grid sm:grid-cols-3 gap-4 mt-6">
          {[
            { t: "البساطة", d: "حلول جاهزة وسريعة بأقل تعقيد" },
            { t: "الذكاء", d: "محتوى واستراتيجيات مدعومة بالذكاء الاصطناعي" },
            { t: "النتائج", d: "نركّز على التفاعل المؤهّل وتحويله إلى طلبات" },
          ].map((v, i) => (
            <div key={i} className="glass rounded-2xl p-5 border border-border text-center" data-testid={`value-${i}`}>
              <div className="font-bold text-primary mb-1">{v.t}</div>
              <div className="text-sm text-muted-foreground">{v.d}</div>
            </div>
          ))}
        </div>
      </section>

      </section>

      {/* Features */}
      <section id="features" className="py-20 px-6 max-w-7xl mx-auto">
        <div className="text-center mb-14">
          <h2 className="text-3xl sm:text-4xl font-bold font-heading mb-3">كل ما تحتاجه لتسويق مشروعك</h2>
          <p className="text-muted-foreground max-w-2xl mx-auto">أدوات متكاملة مدعومة بالذكاء الاصطناعي من الاستراتيجية حتى النشر.</p>
        </div>
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
          {features.map((f, i) => (
            <motion.div key={i} initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }} transition={{ delay: i * 0.08 }}
              className="glass rounded-2xl p-6 card-hover border border-border">
              <div className="w-12 h-12 rounded-xl bg-primary/15 flex items-center justify-center mb-4"><f.icon className="w-6 h-6 text-primary" /></div>
              <h3 className="text-lg font-bold mb-2">{f.title}</h3>
              <p className="text-sm text-muted-foreground leading-relaxed">{f.desc}</p>
            </motion.div>
          ))}
        </div>
      </section>

      {/* Pricing */}
      <section id="pricing" className="py-20 px-6 max-w-7xl mx-auto">
        <div className="text-center mb-10">
          <h2 className="text-3xl sm:text-4xl font-bold font-heading mb-3">باقات تناسب مشروعك</h2>
          <p className="text-muted-foreground">أسعار تأسيسية بالريال السعودي — ابدأ مجاناً وترقَّ متى شئت.</p>
          <div className="inline-flex items-center gap-2 mt-4 bg-accent/10 border border-accent/30 rounded-full px-4 py-1.5 text-sm text-accent font-semibold" data-testid="coupon-hint">
            <Sparkles className="w-4 h-4" /> لديك كوبون إطلاق؟ أدخله عند الاشتراك واحصل على خصمك فوراً
          </div>
        </div>
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5 items-stretch">
          {plans.map((p, i) => (
            <div key={i} className={`rounded-3xl p-7 border flex flex-col ${p.highlight ? "glass border-primary/50 emerald-glow scale-[1.03]" : "glass border-border"}`} data-testid={`plan-${i}`}>
              {p.highlight && <div className="inline-block bg-primary text-white text-xs font-bold px-3 py-1 rounded-full mb-4 self-start">الأكثر شيوعاً</div>}
              <h3 className="text-lg font-bold mb-2">{p.name}</h3>
              <div className="mb-6"><span className="text-4xl font-extrabold">{p.price}</span> <span className="text-muted-foreground text-xs">{p.period}</span></div>
              <ul className="space-y-3 mb-8 flex-1">
                {p.features.map((f, j) => (
                  <li key={j} className="flex items-start gap-2 text-sm"><Check className="w-4 h-4 text-primary shrink-0 mt-0.5" /> {f}</li>
                ))}
              </ul>
              <Link to={user ? "/app/packages" : `/register?next=${encodeURIComponent("/app/packages")}`}><Button className={`w-full font-bold ${p.highlight ? "bg-primary hover:bg-primary/90 text-white" : "bg-foreground/90 hover:bg-foreground text-background"}`}>{p.cta}</Button></Link>
            </div>
          ))}
        </div>
        {pricing && (
          <div className="mt-10 grid lg:grid-cols-[1.2fr_1fr] gap-6 items-start" data-testid="landing-services-pricing">
            <div>
              <h3 className="text-base md:text-lg font-bold mb-1">خدمات حسب الطلب — بدون اشتراك</h3>
              <p className="text-xs text-muted-foreground mb-3">اختر الخدمة والكمية، وادفع حسب احتياجك دون اشتراك شهري. اضغط أي خدمة لترى نطاقها وما يشمله السعر.</p>
              <ServicesTable pricing={pricing} />
            </div>
            <QuotaPolicy pricing={pricing} />
          </div>
        )}
        <div className="mt-8 flex items-start gap-3 justify-center max-w-3xl mx-auto glass border border-border rounded-2xl px-5 py-4" data-testid="payment-gateway-note">
          <ShieldCheck className="w-5 h-5 text-primary shrink-0 mt-0.5" />
          <p className="text-sm text-muted-foreground text-center">
            جميع العمليات المالية تتم عبر <b className="text-foreground">بوابة دفع إلكترونية آمنة</b> ومشفّرة، وتخضع لـ
            <Link to="/terms" className="text-primary hover:underline mx-1">الشروط والأحكام</Link>،
            <Link to="/privacy" className="text-primary hover:underline mx-1">سياسة الخصوصية</Link>،
            <Link to="/refund" className="text-primary hover:underline mx-1">سياسة الاسترجاع/الاسترداد</Link>، و
            <Link to="/security" className="text-primary hover:underline mx-1">سياسة الأمان</Link>.
          </p>
        </div>
        <div className="mt-5 text-center" data-testid="pricing-payment-methods">
          <p className="text-xs text-muted-foreground mb-3">وسائل الدفع المقبولة — يُفعَّل اشتراكك فورًا بعد نجاح الدفع</p>
          <PaymentMethods />
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 px-6">
        <div className="max-w-5xl mx-auto glass rounded-3xl p-12 text-center emerald-glow border border-primary/30">
          <h2 className="text-3xl sm:text-4xl font-bold font-heading mb-4">جاهز لإطلاق تسويقك؟</h2>
          <p className="text-muted-foreground mb-8 max-w-xl mx-auto">انضم إلى رواد الأعمال الذين يبنون حضورهم الرقمي بذكاء وسرعة.</p>
          <Link to={user ? "/app" : "/register"} onClick={() => !user && track("cta_click", "bottom")}><Button size="lg" className="bg-primary hover:bg-primary/90 text-white font-bold gap-2 h-12 px-8" data-testid="landing-bottom-cta"><Rocket className="w-5 h-5" /> ابدأ الآن مجاناً</Button></Link>
        </div>
      </section>

      <StoryHighlights />

      {reviews && reviews.count > 0 && (
      <section id="reviews" className="py-14 px-6 max-w-7xl mx-auto" data-testid="reviews-section">
        <div className="flex items-center justify-center gap-3 mb-6 flex-wrap text-center">
          <h2 className="text-2xl sm:text-3xl font-bold font-heading">آراء عملائنا</h2>
          <div className="flex items-center gap-1.5 bg-amber-400/10 border border-amber-400/30 rounded-full px-3 py-1" data-testid="reviews-average">
            <Star className="w-4 h-4 fill-amber-400 text-amber-400" />
            <span className="font-bold text-amber-500">{reviews.average}</span>
            <span className="text-xs text-muted-foreground">({reviews.count} تقييم)</span>
          </div>
        </div>
        <div className="flex gap-4 overflow-x-auto pb-3 snap-x">
          {reviews.reviews.map((r, i) => (
            <div key={i} className="glass rounded-2xl p-5 border border-border min-w-[260px] max-w-[300px] snap-start shrink-0" data-testid={`review-card-${i}`}>
              <div className="flex gap-0.5 mb-2" dir="ltr">
                {[1, 2, 3, 4, 5].map((n) => (
                  <Star key={n} className={`w-4 h-4 ${n <= r.rating ? "fill-amber-400 text-amber-400" : "text-muted-foreground/30"}`} />
                ))}
              </div>
              {r.text && <p className="text-sm text-foreground/85 leading-relaxed mb-3 line-clamp-4">{r.text}</p>}
              <div className="text-xs font-bold text-muted-foreground">— {r.name}</div>
            </div>
          ))}
        </div>
      </section>
      )}


      <LandingServices />
      <LandingFaq />

      {!user && (
        <div className="fixed bottom-0 inset-x-0 z-40 lg:hidden p-3 bg-background/90 backdrop-blur border-t border-border" data-testid="landing-sticky-cta">
          <Link to="/register" onClick={() => track("cta_click", "sticky")}>
            <Button className="w-full h-12 bg-primary hover:bg-primary/90 text-white font-bold gap-2"><Rocket className="w-5 h-5" /> ابدأ مجاناً — صورتان وفيديو هدية</Button>
          </Link>
        </div>
      )}
      <SiteFooter />
      <ScrollToTop />
    </div>
  );
}
